import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  HardHat, ShieldCheck, KeyRound, FileText, FilePlus2,
  CheckCircle2, AlertTriangle, ArrowRight, Lock, Upload,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [
  { key: "info", label: "Confirm info", icon: HardHat },
  { key: "password", label: "Set password", icon: KeyRound },
  { key: "w9", label: "Upload W-9", icon: FileText },
  { key: "coi", label: "Upload COI", icon: ShieldCheck },
];

export default function VendorOnboardingPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // Step 1 — info
  const [info, setInfo] = useState({
    contact: "", phone: "", address: "", website: "",
    hourly_rate: "", service_categories_text: "",
  });
  // Step 2 — password
  const [pw, setPw] = useState({ password: "", confirm: "" });
  // Steps 3 & 4 — doc uploads
  const [w9, setW9] = useState({ ein_last4: "", signed_at: "", file: null });
  const [coi, setCoi] = useState({ carrier: "", policy_no: "", coverage_usd: "", expires_at: "", file: null });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/vendor-onboarding/${token}`);
      setCtx(data);
      setInfo({
        contact: data.contact || "",
        phone: data.phone || "",
        address: data.address || "",
        website: data.website || "",
        hourly_rate: data.hourly_rate ?? "",
        service_categories_text: (data.service_categories || []).join(", "),
      });
      // Jump to first incomplete step
      const p = data.progress || {};
      if (!p.info_confirmed) setStep(0);
      else if (!p.password_set) setStep(1);
      else if (!p.w9_uploaded) setStep(2);
      else if (!p.coi_uploaded) setStep(3);
      else setStep(3);
    } catch (e) {
      setErr(e?.response?.data?.detail || "This onboarding link is invalid or expired.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submitInfo = async () => {
    setBusy(true);
    try {
      const body = {
        contact: info.contact || null,
        phone: info.phone || null,
        address: info.address || null,
        website: info.website || null,
        hourly_rate: info.hourly_rate === "" ? null : Number(info.hourly_rate),
        service_categories: info.service_categories_text
          .split(",").map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/public/vendor-onboarding/${token}/confirm-info`, body);
      toast.success("Contact info saved");
      setStep(1);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const submitPassword = async () => {
    if (pw.password.length < 8) return toast.error("Password must be at least 8 chars");
    if (pw.password !== pw.confirm) return toast.error("Passwords don't match");
    if (!(/[A-Za-z]/.test(pw.password) && /\d/.test(pw.password))) {
      return toast.error("Password needs at least one letter and one number");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/vendor-onboarding/${token}/set-password`, { password: pw.password });
      toast.success("Password set");
      setStep(2);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Password save failed"); }
    setBusy(false);
  };

  const fileToDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const submitDoc = async (dtype) => {
    const src = dtype === "w9" ? w9 : coi;
    if (!src.file) return toast.error("Attach a file first");
    if (src.file.size > 4 * 1024 * 1024) return toast.error("Max 4 MB");
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(src.file);
      const body = {
        doc_type: dtype,
        doc_data_url: dataUrl,
        doc_filename: src.file.name,
      };
      if (dtype === "w9") {
        body.ein_last4 = w9.ein_last4;
        body.signed_at = w9.signed_at || new Date().toISOString().slice(0, 10);
      } else {
        body.carrier = coi.carrier;
        body.policy_no = coi.policy_no;
        body.coverage_usd = coi.coverage_usd ? Number(coi.coverage_usd) : null;
        body.expires_at = coi.expires_at;
      }
      await axios.post(`${API}/public/vendor-onboarding/${token}/upload-doc`, body);
      toast.success(`${dtype.toUpperCase()} uploaded`);
      if (dtype === "w9") setStep(3);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  const finish = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/vendor-onboarding/${token}/complete`);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot finalize yet — complete every step first"); }
    setBusy(false);
  };

  if (err) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center px-4">
        <Card className="border-white/10 bg-card p-8 max-w-md text-center" data-testid="onboarding-error">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
          <div className="text-xl font-semibold mb-1">Link unavailable</div>
          <div className="text-sm text-zinc-400">{err}</div>
          <div className="text-xs text-zinc-500 mt-4">Please ask your property manager to resend the invite.</div>
        </Card>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center px-4">
        <Card className="border-emerald-500/30 bg-emerald-500/[0.05] p-8 max-w-md text-center" data-testid="onboarding-done">
          <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
          <div className="text-2xl font-semibold mb-2">You're all set</div>
          <div className="text-sm text-zinc-300 mb-6">
            Your vendor account is active. Sign in to the vendor portal to view work orders and submit bids.
          </div>
          <Link to="/vendor" className="inline-flex items-center gap-2 text-orange-400 hover:underline text-sm">
            Go to vendor portal <ArrowRight className="h-3 w-3" />
          </Link>
        </Card>
      </div>
    );
  }
  if (!ctx) {
    return <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center text-sm text-zinc-500">Loading…</div>;
  }

  const p = ctx.progress || {};
  const stepCompleted = [p.info_confirmed, p.password_set, p.w9_uploaded, p.coi_uploaded];
  const allDone = stepCompleted.every(Boolean);

  return (
    <div className="min-h-screen bg-black text-zinc-200 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-orange-400 mb-2">
            <HardHat className="h-4 w-4" /> Vendor onboarding
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold font-display">Welcome, {ctx.vendor_name}</h1>
          <p className="text-sm text-zinc-500 mt-2">
            Serving {(ctx.properties || []).map((x) => x.name).join(" · ") || "Prestigious PM"}
          </p>
        </div>

        {/* Progress rail */}
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === i;
            const done_ = stepCompleted[i];
            return (
              <div key={s.key} className="flex-1 flex flex-col items-center" data-testid={`step-${s.key}`}>
                <div className={
                  "h-9 w-9 rounded-full flex items-center justify-center border transition-colors " +
                  (done_ ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                    : active ? "bg-orange-500/20 border-orange-500/40 text-orange-300 ring-2 ring-orange-500/30"
                    : "bg-white/[0.02] border-white/10 text-zinc-500")
                }>
                  {done_ ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <div className={"mt-1 text-[10px] text-center " + (active ? "text-orange-300" : done_ ? "text-emerald-300" : "text-zinc-500")}>{s.label}</div>
              </div>
            );
          })}
        </div>

        {/* STEP 1 — confirm info */}
        {step === 0 && (
          <Card className="border-white/10 bg-card p-6" data-testid="onboarding-info-form">
            <div className="text-lg font-semibold mb-3">Confirm your contact info</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contact name</Label>
                <Input value={info.contact} onChange={(e) => setInfo({ ...info, contact: e.target.value })} data-testid="info-contact" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} data-testid="info-phone" />
              </div>
              <div className="col-span-2">
                <Label>Business address</Label>
                <Input value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} data-testid="info-address" />
              </div>
              <div>
                <Label>Website</Label>
                <Input value={info.website} onChange={(e) => setInfo({ ...info, website: e.target.value })} placeholder="https://…" data-testid="info-website" />
              </div>
              <div>
                <Label>Hourly rate ($)</Label>
                <Input type="number" value={info.hourly_rate} onChange={(e) => setInfo({ ...info, hourly_rate: e.target.value })} data-testid="info-hourly" />
              </div>
              <div className="col-span-2">
                <Label>Additional service categories (comma-separated)</Label>
                <Textarea rows={2} value={info.service_categories_text}
                          onChange={(e) => setInfo({ ...info, service_categories_text: e.target.value })}
                          placeholder="Plumbing, Drain cleaning, Water heater installation"
                          data-testid="info-services" />
                <div className="text-[10px] text-zinc-500 mt-1">Your primary category: <b>{ctx.category}</b>. Add extras so we can dispatch you for related tickets.</div>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <Button onClick={submitInfo} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="info-submit">
                {busy ? "Saving…" : "Save & continue"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </Card>
        )}

        {/* STEP 2 — password */}
        {step === 1 && (
          <Card className="border-white/10 bg-card p-6" data-testid="onboarding-password-form">
            <div className="text-lg font-semibold mb-1">Choose a password</div>
            <div className="text-sm text-zinc-500 mb-3">Used to sign in to the vendor portal at <code className="text-orange-300">/vendor</code>. Minimum 8 chars with at least one letter and one number.</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>New password</Label>
                <Input type="password" value={pw.password}
                       onChange={(e) => setPw({ ...pw, password: e.target.value })}
                       autoComplete="new-password" data-testid="pw-new" />
              </div>
              <div>
                <Label>Confirm</Label>
                <Input type="password" value={pw.confirm}
                       onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                       autoComplete="new-password" data-testid="pw-confirm" />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <div className="text-[10px] text-zinc-500 flex items-center gap-1">
                <Lock className="h-3 w-3" /> Encrypted at rest with bcrypt · Never emailed
              </div>
              <Button onClick={submitPassword} disabled={busy}
                      className="bg-orange-500 hover:bg-orange-600" data-testid="pw-submit">
                {busy ? "Saving…" : "Save & continue"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </Card>
        )}

        {/* STEP 3 — W-9 */}
        {step === 2 && (
          <Card className="border-white/10 bg-card p-6" data-testid="onboarding-w9-form">
            <div className="text-lg font-semibold mb-1">Upload your W-9</div>
            <div className="text-sm text-zinc-500 mb-3">Required by the IRS before we can pay you $600+/year. PDF or image, max 4 MB.</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>EIN last 4 digits</Label>
                <Input maxLength={4} value={w9.ein_last4}
                       onChange={(e) => setW9({ ...w9, ein_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                       placeholder="6789" data-testid="w9-ein" />
              </div>
              <div>
                <Label>Signed on</Label>
                <Input type="date" value={w9.signed_at}
                       onChange={(e) => setW9({ ...w9, signed_at: e.target.value })}
                       data-testid="w9-signed" />
              </div>
              <div className="col-span-2">
                <Label>W-9 file *</Label>
                <Input type="file" accept="image/*,application/pdf"
                       onChange={(e) => setW9({ ...w9, file: e.target.files?.[0] })}
                       data-testid="w9-file" />
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <Button onClick={() => submitDoc("w9")} disabled={busy || !w9.file}
                      className="bg-orange-500 hover:bg-orange-600" data-testid="w9-submit">
                <Upload className="h-3.5 w-3.5 mr-1" /> {busy ? "Uploading…" : "Upload W-9 & continue"}
              </Button>
            </div>
          </Card>
        )}

        {/* STEP 4 — COI */}
        {step === 3 && (
          <Card className="border-white/10 bg-card p-6" data-testid="onboarding-coi-form">
            <div className="text-lg font-semibold mb-1">Upload your Certificate of Insurance (COI)</div>
            <div className="text-sm text-zinc-500 mb-3">General liability of $1M+ typical. Include your carrier + expiry so we can nudge you before it lapses.</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Insurance carrier</Label>
                <Input value={coi.carrier} onChange={(e) => setCoi({ ...coi, carrier: e.target.value })}
                       placeholder="State Farm" data-testid="coi-carrier" />
              </div>
              <div>
                <Label>Policy #</Label>
                <Input value={coi.policy_no} onChange={(e) => setCoi({ ...coi, policy_no: e.target.value })}
                       placeholder="SF-1234567" data-testid="coi-policy" />
              </div>
              <div>
                <Label>Coverage $</Label>
                <Input type="number" value={coi.coverage_usd}
                       onChange={(e) => setCoi({ ...coi, coverage_usd: e.target.value })}
                       placeholder="1000000" data-testid="coi-coverage" />
              </div>
              <div>
                <Label>Expires on *</Label>
                <Input type="date" value={coi.expires_at}
                       onChange={(e) => setCoi({ ...coi, expires_at: e.target.value })}
                       data-testid="coi-expires" />
              </div>
              <div className="col-span-2">
                <Label>COI file *</Label>
                <Input type="file" accept="image/*,application/pdf"
                       onChange={(e) => setCoi({ ...coi, file: e.target.files?.[0] })}
                       data-testid="coi-file" />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between gap-3">
              <div className="text-[10px] text-zinc-500 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> COI is required before we can dispatch any work orders to you.
              </div>
              <div className="flex gap-2">
                <Button onClick={() => submitDoc("coi")} disabled={busy || !coi.file}
                        className="bg-orange-500 hover:bg-orange-600" data-testid="coi-submit">
                  <Upload className="h-3.5 w-3.5 mr-1" /> {busy ? "Uploading…" : "Upload COI"}
                </Button>
                {allDone && (
                  <Button onClick={finish} disabled={busy}
                          className="bg-emerald-600 hover:bg-emerald-500 gap-1" data-testid="finish-btn">
                    <FilePlus2 className="h-4 w-4" /> Finish onboarding
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        <div className="text-center text-[10px] text-zinc-500">
          Link expires {(ctx.expires_at || "").slice(0, 10)} · Progress is saved after each step — you can re-open this link if you close it
        </div>
      </div>
    </div>
  );
}
